import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Download, ShieldCheck, FileText, ChevronDown, ChevronLeft, ChevronRight, Plus, Sparkles } from 'lucide-react';
import { orderService } from '../../../services/OrderService';
import ImageZoomModal from '../../common/ImageZoomModal';
import PDFThumbnail from './PDFThumbnail';
import PDFViewer from './PDFViewer';

interface CaseFile { name: string; mime: string; url: string; gs_uri: string; }
interface SummaryRow { label: string; value: string; }

/**
 * Case Files strip — a collapsed header shown inline in the order header for
 * insurance-sourced orders. Left: the insurer label. The header toggles a
 * thumbnail gallery of the case's source files (private signed URLs); a Case
 * Details toggle on the right shows the case summary.
 *
 * Insurer-agnostic: the backend returns already-normalized {label, value} rows
 * (each portal's harvester maps its own fields), so this renders them verbatim
 * with no per-insurer knowledge. Renders nothing until it has data.
 */
const CaseFilesStrip: React.FC<{
  orderId: number;
  onAttach?: (gcsUri: string, previewUrl: string, name: string, mime: string) => void;
}> = ({ orderId, onAttach }) => {
  const { t } = useTranslation();
  const [files, setFiles] = useState<CaseFile[]>([]);
  const [summary, setSummary] = useState<SummaryRow[]>([]);
  const [parts, setParts] = useState<string[]>([]);
  const [description, setDescription] = useState<string>('');
  const [insurer, setInsurer] = useState<string>('');
  const [vin, setVin] = useState<string>('');
  const [caseId, setCaseId] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [zoomIdx, setZoomIdx] = useState<number | null>(null);
  const [pdf, setPdf] = useState<CaseFile | null>(null);
  const [filesOpen, setFilesOpen] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [entered, setEntered] = useState(false);

  // Fade + slide the bar in once its data is ready, instead of a hard pop.
  useEffect(() => {
    if (loading) return;
    const id = requestAnimationFrame(() => setEntered(true));
    return () => cancelAnimationFrame(id);
  }, [loading]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    orderService.getCaseFiles(orderId)
      .then((res: any) => {
        if (cancelled) return;
        setFiles(res.files || []);
        setSummary(res.summary || []);
        setParts(res.parts || []);
        setDescription(res.description || '');
        setInsurer(res.insurer || '');
        setVin(res.vin || '');
        setCaseId(res.case_id || '');
      })
      .catch(() => { if (!cancelled) setFiles([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [orderId]);

  // Only appear once data is loaded — no placeholder spinner bar.
  if (loading || (files.length === 0 && summary.length === 0)) return null;

  const rows: SummaryRow[] = [];
  if (caseId) rows.push({ label: t('orders.caseFiles.caseNo', { defaultValue: 'Case' }), value: caseId });
  if (vin) rows.push({ label: 'VIN', value: vin });
  rows.push(...summary);

  const images = files.filter(f => f.mime.startsWith('image/'));
  const showImage = (i: number) => setZoomIdx(((i % images.length) + images.length) % images.length);

  return (
    <div className={`border-b border-[#efebe2] bg-[#faf9f6] px-4 py-2.5 transition-all duration-300 ease-out ${entered ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-1'}`}>
      <div className="flex items-center gap-4">
        {/* Green indicator — this is an insurance case (insurer in the tooltip) */}
        <span className="flex items-center"
          title={`${t('orders.caseFiles.insuranceCase', { defaultValue: 'Insurance Case' })}${insurer ? ` (${insurer.toUpperCase()})` : ''}`}>
          <ShieldCheck className="w-4 h-4 text-green-600" />
        </span>
        <button onClick={() => setFilesOpen(o => !o)}
          className="flex items-center gap-1.5 text-[11px] font-semibold text-[#6B6862] uppercase tracking-wide hover:text-[#3a3833]">
          {t('orders.caseFiles.title', { defaultValue: 'Case Files' })}{files.length ? ` (${files.length})` : ''}
          {files.length > 0 && <ChevronDown className={`w-3.5 h-3.5 transition-transform ${filesOpen ? 'rotate-180' : ''}`} />}
        </button>
        {(rows.length > 0 || parts.length > 0 || description) && (
          <button onClick={() => setDetailOpen(o => !o)}
            className="flex items-center gap-1.5 text-[11px] font-semibold text-[#6B6862] uppercase tracking-wide hover:text-[#3a3833]">
            {t('orders.caseFiles.details', { defaultValue: 'Case Details' })}
            <ChevronDown className={`w-3.5 h-3.5 transition-transform ${detailOpen ? 'rotate-180' : ''}`} />
          </button>
        )}
      </div>

      {detailOpen && (rows.length > 0 || parts.length > 0 || description) && (
        <div className="mt-2 bg-white rounded-lg border border-[#efebe2] p-3">
          {description && (
            <div className="mb-3 pb-3 border-b border-[#efebe2]">
              <span className="flex items-center gap-1 text-[10px] uppercase tracking-wide text-[#9a968e]">
                <Sparkles className="w-3 h-3 text-teal-500" />{t('orders.caseFiles.description', { defaultValue: 'AI Summary' })}
              </span>
              <p className="text-xs text-[#3a3833] mt-0.5 leading-relaxed">{description}</p>
            </div>
          )}
          {rows.length > 0 && (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-1.5 text-xs">
              {rows.map((r, i) => (
                <div key={`${r.label}-${i}`} className="flex flex-col">
                  <span className="text-[10px] uppercase tracking-wide text-[#9a968e]">{r.label}</span>
                  <span className="text-[#3a3833] break-words">{r.value}</span>
                </div>
              ))}
            </div>
          )}
          {parts.length > 0 && (
            <div className={(rows.length > 0 || description) ? 'mt-3 pt-3 border-t border-[#efebe2]' : ''}>
              <span className="text-[10px] uppercase tracking-wide text-[#9a968e]">{t('orders.caseFiles.parts', { defaultValue: 'Parts' })}</span>
              <ul className="list-disc list-inside text-xs text-[#3a3833] mt-0.5">
                {parts.map((p, i) => <li key={i} className="break-words">{p}</li>)}
              </ul>
            </div>
          )}
        </div>
      )}

      {filesOpen && (
        <div className="flex flex-wrap gap-2 mt-2">
          {files.map((f, i) => {
            const isImage = f.mime.startsWith('image/');
            const isPdf = f.mime === 'application/pdf';
            let tile: React.ReactNode;
            if (isImage) {
              tile = (
                <button onClick={() => setZoomIdx(images.indexOf(f))} title={f.name}
                  className="w-20 h-20 rounded-lg overflow-hidden border border-[#efebe2] hover:border-teal-300 transition-colors block">
                  <img src={f.url} alt={f.name} className="w-full h-full object-cover" loading="lazy" />
                </button>
              );
            } else if (isPdf) {
              tile = (
                <div title={f.name}
                  className="w-20 h-20 rounded-lg overflow-hidden border border-[#efebe2] hover:border-teal-300 transition-colors bg-white">
                  <PDFThumbnail file={f.url} onClick={() => setPdf(f)} />
                </div>
              );
            } else {
              tile = (
                <button onClick={() => window.open(f.url, '_blank', 'noopener')} title={f.name}
                  className="w-20 h-20 rounded-lg border border-[#efebe2] hover:border-teal-300 transition-colors flex flex-col items-center justify-center gap-1 bg-white px-1">
                  <Download className="w-5 h-5 text-gray-400" />
                  <span className="text-[9px] text-[#6B6862] text-center line-clamp-2 break-all">{f.name}</span>
                </button>
              );
            }
            return (
              <div key={`${f.name}-${i}`} className="relative group/tile">
                {tile}
                {onAttach && (
                  <button
                    onClick={(e) => { e.stopPropagation(); onAttach(f.gs_uri, f.url, f.name, f.mime); }}
                    title={t('orders.caseFiles.addToOrder', { defaultValue: 'Add to order' })}
                    aria-label={t('orders.caseFiles.addToOrder', { defaultValue: 'Add to order' })}
                    className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-teal-600 text-white flex items-center justify-center shadow-sm opacity-0 group-hover/tile:opacity-100 transition-opacity hover:bg-teal-700">
                    <Plus className="w-3 h-3" strokeWidth={3} />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {zoomIdx !== null && images[zoomIdx] && (
        <>
          <ImageZoomModal src={images[zoomIdx].url} alt={images[zoomIdx].name} onClose={() => setZoomIdx(null)} />
          {images.length > 1 && (
            <>
              <button onClick={() => showImage(zoomIdx - 1)} aria-label="Previous"
                className="fixed left-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/15 hover:bg-white/30 text-white flex items-center justify-center backdrop-blur"
                style={{ zIndex: 100001 }}>
                <ChevronLeft className="w-6 h-6" />
              </button>
              <button onClick={() => showImage(zoomIdx + 1)} aria-label="Next"
                className="fixed right-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/15 hover:bg-white/30 text-white flex items-center justify-center backdrop-blur"
                style={{ zIndex: 100001 }}>
                <ChevronRight className="w-6 h-6" />
              </button>
              <div className="fixed bottom-4 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-white/15 text-white text-xs backdrop-blur"
                style={{ zIndex: 100001 }}>
                {zoomIdx + 1} / {images.length}
              </div>
            </>
          )}
        </>
      )}
      {pdf && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60" onClick={() => setPdf(null)}>
          <div className="bg-white rounded-2xl overflow-hidden max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-4 py-2 border-b border-[#efebe2]">
              <span className="text-xs font-medium truncate flex items-center gap-1.5"><FileText className="w-3.5 h-3.5 text-red-400" />{pdf.name}</span>
              <button onClick={() => setPdf(null)} className="p-1 text-gray-400 hover:text-gray-600"><X className="w-4 h-4" /></button>
            </div>
            <PDFViewer file={pdf.url} />
          </div>
        </div>
      )}
    </div>
  );
};

export default CaseFilesStrip;
